import { useEffect, useState } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import useContactMerges, { useUnmergePreview } from "../../data/useContactMerges";
import Modal from "../../layouts/Modal";
import { suggestedLaterLeads, toggleId } from "../../utils/contactMerges";
import formatDateTime from "../../utils/formatDateTime";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SegmentedToggleInput from "../shared/Inputs/SegmentedToggleInput";
import LoadingWheel from "../shared/LoadingWheel";
import SharedContactLeadsTable from "./SharedContactLeadsTable";

interface ContactUnmergeModalProps {
  mergeId: string | null;
  setMergeId: (mergeId: string | null) => void;
}

// Brings one merged contact back with what its merge moved. Leads that reached the kept contact after
// the merge are the admin's call, one by one, starting from where the API suggests they belong.
export function ContactUnmergeModal({ mergeId, setMergeId }: ContactUnmergeModalProps) {
  const { t } = useTranslation("accounts");
  const { addNotification } = useNotification();
  const { unmerge } = useContactMerges(undefined, false, "ContactUnmergeModal");
  const preview = useUnmergePreview(mergeId, "ContactUnmergeModal");
  const data = preview.data?.data;
  const [moving, setMoving] = useState<string[]>([]);

  useEffect(() => {
    setMoving(data ? suggestedLaterLeads(data.laterLeads) : []);
  }, [data]);

  const open = !!mergeId;
  const setOpen = (value: React.SetStateAction<boolean>) => {
    const next = typeof value === "function" ? value(open) : value;
    if (!next) setMergeId(null);
  };

  const sourceName = data?.sourceName || t("the merged contact");
  const targetName = data?.targetName || t("the kept contact");

  const confirm = () => {
    if (!mergeId) return;

    unmerge.mutate(
      { mergeId, laterLeadIdsToMove: moving },
      {
        onSuccess: (res) => {
          addNotification({ variant: "success", primaryText: res.message ?? t("Unmerged") });
          setMergeId(null);
        },
        onError: (error: any) =>
          addNotification({ variant: "error", primaryText: t("The unmerge did not finish"), secondaryText: error?.message }),
      }
    );
  };

  const otherRows = data
    ? [
        data.documents > 0 && t("{{count}} document(s)", { count: data.documents }),
        data.messages > 0 && t("{{count}} message(s)", { count: data.messages }),
        data.emails > 0 && t("{{count}} email(s)", { count: data.emails }),
        data.orders > 0 && t("{{count}} order(s)", { count: data.orders }),
      ].filter(Boolean)
    : [];

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-3xl">
      {preview.isLoading || !data ? (
        preview.isError ? (
          <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{t("Could not load what the unmerge would do.")}</p>
        ) : (
          <div className="min-h-[12rem]">
            <LoadingWheel />
          </div>
        )
      ) : (
        <div className="space-y-5">
          <div>
            <h2 className="text-xl font-semibold">{t("Unmerge {{source}} from {{target}}", { source: sourceName, target: targetName })}</h2>
            <p className="text-sm text-gray-500">
              {t("Merged {{date}}", { date: formatDateTime(data.mergedAt, DateTime.DATETIME_SHORT) })}
            </p>
          </div>

          {data.refusal && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{data.refusal}</p>}

          <section>
            <h3 className="text-sm font-semibold text-gray-900">
              {t("Going back to {{name}}: {{count}} lead(s)", { name: sourceName, count: data.goingBack.length })}
              {otherRows.length > 0 && `, ${otherRows.join(", ")}`}
            </h3>
            {data.goingBack.length > 0 && (
              <div className="mt-2">
                <SharedContactLeadsTable leads={data.goingBack} variant="list" />
              </div>
            )}
          </section>

          {data.staying.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-900">
                {t("Staying where staff moved them since the merge: {{count}} lead(s)", { count: data.staying.length })}
              </h3>
              <div className="mt-2">
                <SharedContactLeadsTable leads={data.staying} variant="list" />
              </div>
            </section>
          )}

          {data.laterLeads.length > 0 && (
            <section>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t("Leads that arrived after the merge ({{count}})", { count: data.laterLeads.length })}
                </h3>
                <div className="flex gap-3 text-sm font-semibold text-primary-600">
                  <button type="button" onClick={() => setMoving(data.laterLeads.map((l) => l.id))}>
                    {t("All to {{name}}", { name: sourceName })}
                  </button>
                  <button type="button" onClick={() => setMoving([])}>
                    {t("All stay")}
                  </button>
                </div>
              </div>

              <ul className="mt-2 divide-y divide-gray-200 rounded-md border border-gray-200">
                {data.laterLeads.map((lead) => (
                  <li key={lead.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {[lead.vrm, [lead.make, lead.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
                        {" · "}
                        {formatDateTime(lead.created, DateTime.DATE_MED)}
                      </p>
                      <p className="text-xs text-gray-500">{lead.reason}</p>
                    </div>
                    <SegmentedToggleInput
                      label={null}
                      register={null}
                      registerName={lead.id}
                      watch={() => moving.includes(lead.id)}
                      setValue={() => setMoving((current) => toggleId(current, lead.id))}
                      options={[
                        { value: false, label: targetName },
                        { value: true, label: sourceName },
                      ]}
                      widthClass="w-72"
                      ariaLabel={t("Where this lead goes")}
                    />
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-gray-500">{t("Pre-picked from how each lead arrived; change any before confirming.")}</p>
            </section>
          )}

          {data.fieldsRestored.length > 0 && (
            <p className="text-sm text-gray-700">
              {t("{{name}} loses what the merge filled in and nobody has changed since: {{fields}}.", {
                name: targetName,
                fields: data.fieldsRestored.join(", "),
              })}
            </p>
          )}
        </div>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <ButtonInput label={t("Cancel")} isSubmit={false} onClick={() => setMergeId(null)} classes="" />
        <ButtonInput
          label={t("Unmerge")}
          isSubmit={false}
          onClick={confirm}
          classes=""
          disabled={!data || !!data.refusal}
          isLoading={unmerge.isLoading}
        />
      </div>
    </Modal>
  );
}
